import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

import { useResponsive } from 'src/hooks/use-responsive';

import type { UseDateRangePickerReturn } from './types';

// ----------------------------------------------------------------------

export const CustomDateRangePicker = memo(
  ({
    open,
    error,
    endDate,
    onClose,
    onApply,
    startDate,
    onChangeEndDate,
    variant = 'input',
    onChangeStartDate,
    title = 'Select date range',
    format = 'DD MMM YYYY',
    dialogWidthPx,
  }: UseDateRangePickerReturn) => {
    const {t} = useTranslation('common');
    const mdUp = useResponsive('up', 'md');

    const isCalendarView = variant === 'calendar';
    const shouldFixWidth = !isCalendarView && typeof dialogWidthPx === 'number' && dialogWidthPx > 0;
    // Menu popup uses an inset stroke without an outer border, while Dialog uses a 1px border.
    // We pass the same width number so the outer edges visually align.
    const outerWidthPx = shouldFixWidth ? Math.max(0, dialogWidthPx) : undefined;

    const handleApply = useCallback(() => {
      if (onApply) {
        onApply();
      }
      onClose();
    }, [onApply, onClose]);

    if (!open) {
      return null;
    }

    return (
      <Dialog
        fullWidth={!shouldFixWidth}
        maxWidth={isCalendarView ? false : shouldFixWidth ? false : 'xs'}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            ...(isCalendarView && { maxWidth: 720 }),
            ...(shouldFixWidth && {
              // Dialog uses a 1px border; subtract 2px so outer edges visually match
              // other dropdowns that use inset strokes.
              width: `${outerWidthPx}px !important`,
              minWidth: `${outerWidthPx}px !important`,
              maxWidth: `${outerWidthPx}px !important`,
            }),
            // Same light stroke as the dashboard tabs.
            border: '1px solid rgba(145, 158, 171, 0.32)',
            boxSizing: 'border-box',
          },
          elevation: 8,
        }}
        transitionDuration={200}
        keepMounted={false}
      >
        <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

        <DialogContent sx={{ ...(isCalendarView && mdUp && { overflow: 'unset' }) }}>
          <Stack
            justifyContent="center"
            spacing={isCalendarView ? 3 : 2}
            direction={isCalendarView && mdUp ? 'row' : 'column'}
            sx={{ pt: 1 }}
          >
            {isCalendarView ? (
              <>
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, borderColor: 'divider', borderStyle: 'dashed' }}
                >
                  <DateCalendar value={startDate} onChange={onChangeStartDate} />
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, borderColor: 'divider', borderStyle: 'dashed' }}
                >
                  <DateCalendar value={endDate} onChange={onChangeEndDate} />
                </Paper>
              </>
            ) : (
              <>
                <DatePicker
                  label={t('customDateRangePicker.startDate')}
                  value={startDate}
                  onChange={onChangeStartDate}
                  format={format}
                />

                <DatePicker
                  label={t('customDateRangePicker.endDate')}
                  value={endDate}
                  onChange={onChangeEndDate}
                  format={format}
                />
              </>
            )}
          </Stack>

          {error && (
            <FormHelperText error sx={{ px: 2 }}>
              {t('customDateRangePicker.endDateError')}
            </FormHelperText>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            // Make the top stroke span edge-to-edge (theme DialogActions uses padding).
            borderTop: '1px solid rgba(145, 158, 171, 0.32)',
            mx: '-3',
            px: '3',
          }}
        >
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {t('customDateRangePicker.cancel')}
          </Button> 

          <Button disabled={error} variant="contained" onClick={handleApply}>
            {t('customDateRangePicker.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
